import { Injectable } from '@angular/core';
import { FiStateService } from '../../../fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiChecklist2ReportType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { IChecklistV2ViewerBase } from '@fi-shared/checklist/checklist-v2';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiChecklist2ReportTimePeriodType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-time-period-type';
import { IChecklist2ReportDateRange } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';

interface IChecklist2ReportStateParams {
    checklistId?: number;
    scheduleId?: number;
    reportType?: FiChecklist2ReportType;
    timePeriodType?: FiChecklist2ReportTimePeriodType;
    startDate?: string;
    endDate?: string;
}

@Injectable()
export class FiChecklist2ReportStateService {
    constructor(
        private fiStateService: FiStateService,
        private fiChecklistHelperService: FiChecklistHelperService,
    ) {}

    goToReport(checklistId: number, scheduleId: number, item: IChecklistV2ViewerBase, auditMode: boolean) {
        const isOperationalPeriodActive = this.fiChecklistHelperService.isOperationalPeriodActive(item);
        let reportType: FiChecklist2ReportType;
        if (isOperationalPeriodActive) {
            reportType = FiChecklist2ReportType.OpenChecklists;
        } else {
            reportType = FiChecklist2ReportType.PastChecklists;
        }
        this.goToState(
            {
                reportType: reportType,
                checklistId: checklistId,
                scheduleId: scheduleId,
            },
            auditMode,
        );
    }

    private goToState(params: IChecklist2ReportStateParams, auditMode: boolean) {
        this.fiStateService.go(FiStateNames.getChecklistV2RootState(auditMode).Report.Dashboard, params);
    }

    shouldShowOpenChecklistsReport() {
        return this.getReportStateParams().reportType === FiChecklist2ReportType.OpenChecklists;
    }

    shouldShowOpenChecklistsUnits() {
        const params = this.getReportStateParams();
        return this.shouldShowOpenChecklistsReport() && params.checklistId && params.scheduleId;
    }

    shouldShowAttentionRequiredReport() {
        return this.getReportStateParams().reportType === FiChecklist2ReportType.AttentionRequired;
    }

    shouldShowAttentionRequiredDetails() {
        const params = this.getReportStateParams();
        return this.shouldShowAttentionRequiredReport() && params.checklistId && params.scheduleId;
    }

    shouldShowPastChecklistsReport() {
        return this.getReportStateParams().reportType === FiChecklist2ReportType.PastChecklists;
    }

    shouldShowPastChecklistsUnits() {
        const params = this.getReportStateParams();
        return this.shouldShowPastChecklistsReport() && params.checklistId && params.scheduleId;
    }

    getChecklistIdParam(): number {
        return this.getReportStateParams().checklistId;
    }

    getScheduleIdParam(): number {
        return this.getReportStateParams().scheduleId;
    }

    getDateRangeParam(): IChecklist2ReportDateRange {
        return {
            TimePeriodType: this.getTimePeriodTypeParam(),
            StartDate: this.getStartDateParam(),
            EndDate: this.getEndDateParam(),
        };
    }
    hasPeriodTimeParams() {
        return !!this.getTimePeriodTypeParam();
    }
    private getTimePeriodTypeParam(): FiChecklist2ReportTimePeriodType {
        return this.getReportStateParams().timePeriodType;
    }
    private getStartDateParam(): Date {
        return this.getReportStateParams().startDate ? new Date(this.getReportStateParams().startDate) : undefined;
    }
    private getEndDateParam(): Date {
        return this.getReportStateParams().endDate ? new Date(this.getReportStateParams().endDate) : undefined;
    }

    // here we can change state params on demand
    onShowWidgetSummaryPage() {}
    onCloseWidgetSummaryPage() {}
    onShowWidgetHistoryUnitsPage() {}
    onShowWidgetOpenChecklistUnitsPage() {}
    onCloseWidgetUnitsPage() {
        if (
            this.shouldShowOpenChecklistsUnits() ||
            this.shouldShowPastChecklistsUnits() ||
            this.shouldShowAttentionRequiredDetails()
        ) {
            // user choose "view report" action, so we should go back when they close report
            window.history.back();
        }
    }

    private getReportStateParams(): IChecklist2ReportStateParams {
        return this.fiStateService?.params ?? ({} as IChecklist2ReportStateParams);
    }
}
